import type {SyntheticEvent} from 'react'
import * as React from 'react'
import {useCallback, useMemo} from 'react'
import {useLocation, useNavigate} from 'react-router-dom'

import {getHrefWithQueryParams} from '../../../../../routes'
import {
  getFullBuildLogLinkParams,
  getKeyForBuildLogData,
  getNonEmptyMessages,
  INITIAL_NEXT_MESSAGES_COUNT,
} from '../../BuildLog.utils'
import BuildLogMessages from '../../BuildLogMessages/BuildLogMessages'
import {ROW_HEIGHT} from '../../BuildLogMessages/BuildLogMessages.utils'

import type {Props} from './BuildLogRunningMessages.types'

import styles from './BuildLogRunningMessages.css'

export const MESSAGES_COUNT = 9
const MESSAGES_PADDING_PX = 8
const target = 'popup'

export default function BuildLogRunningMessages({
  running,
  messages,
  extensionEnabled,
  buildId,
  fetchBuildLogMessages,
  ready,
}: Props) {
  const location = useLocation()
  const navigate = useNavigate()

  const filtered = getNonEmptyMessages(messages)
  const count = Math.min(filtered.length, MESSAGES_COUNT)

  const fullBuildLogLinkParams = useMemo(
    () =>
      getFullBuildLogLinkParams({
        line: filtered.length > 0 ? filtered[filtered.length - 1].id : undefined,
        logFilter: 'debug',
        expandAll: true,
        target,
        buildId,
      }),
    [buildId, filtered],
  )

  const onClick = useCallback(
    (e: SyntheticEvent) => {
      e.stopPropagation()
      e.preventDefault()
      fetchBuildLogMessages({
        buildLogKey: getKeyForBuildLogData({
          type: 'full',
          target,
        }),
        invalidate: true,
        buildId,
        target,
        options: {
          logAnchor: -1,
          count: [0, -INITIAL_NEXT_MESSAGES_COUNT],
          expandAll: true,
          filter: 'debug',
        },
      })
      navigate(
        getHrefWithQueryParams(location, null, params => ({...params, ...fullBuildLogLinkParams})),
      )
    },
    [buildId, fetchBuildLogMessages, fullBuildLogLinkParams, location, navigate],
  )

  if (!extensionEnabled || !running) {
    return null
  }

  return (
    <div
      className={styles.wrapper}
      style={{
        height: Math.max(count, 1) * ROW_HEIGHT + MESSAGES_PADDING_PX,
      }}
      role={'presentation'}
      onClick={onClick}
    >
      {!ready ? (
        <div className={styles.info}>{'Loading messages...'}</div>
      ) : filtered.length === 0 ? (
        <div className={styles.info}>{'No messages yet'}</div>
      ) : (
        <BuildLogMessages
          messages={
            filtered.length > MESSAGES_COUNT
              ? filtered.slice(filtered.length - MESSAGES_COUNT)
              : filtered
          }
          className={styles.messages}
          messageClassName={styles.message}
          withDuration
        />
      )}
    </div>
  )
}
